function parseTime(timeString) {
  // Split the time string by colon and convert each part to a number
  // If seconds are not provided, default to 0
  const parts = timeString.split(":");
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  return { hours, minutes };
}

/**
 * Function that converts time in "HH:MM" format to Decimal format
 * @param {string} timeStr - Time in "HH:MM" format
 * @returns {number} - Time in Decimal format
 */
const convertTimeToDecimal = (timeStr) => {
  if (!timeStr || typeof timeStr !== "string") return 0;
  const { hours, minutes } = parseTime(timeStr);
  if (isNaN(hours) || isNaN(minutes)) return 0;
  return hours + minutes / 60;
};

/** * Compares two time strings in "HH:MM" format.
 * @param {string} t1 - The first time string.
 * @param {string} t2 - The second time string.
 * @returns {boolean} True if t1 is earlier than t2, false otherwise.
 * */
function isFirstInputEarlier(t1, t2) {
  const [hours1, minutes1] = t1.split(":").map(Number);
  const [hours2, minutes2] = t2.split(":").map(Number);

  // Return True if hours and minutes are same for both inputs
  if (hours1 === hours2 && minutes1 === minutes2) {
    return true;
  }

  // Compare hours first
  if (hours1 < hours2) {
    return true;
  } else if (hours1 > hours2) {
    return false;
  } else {
    // If hours are equal, compare minutes
    return minutes1 < minutes2;
  }
}

/* ================================================================== */
const getTimeWithRelaxation = (
  time,
  userRelaxationMinutes,
  applyUserLateTimeRule,
  deviceRelaxationMinutes
) => {
  // Apply User Relaxation Minutes if available
  let relaxationMinutes = deviceRelaxationMinutes;

  if (applyUserLateTimeRule) {
    relaxationMinutes = userRelaxationMinutes || 0;
  }

  // calculate inTime with relaxation minutes
  let relaxationTimeInHours = Math.floor(relaxationMinutes / 60);
  let relaxationTimeInRemainingMinutes = relaxationMinutes % 60;

  let relaxationTime = `${relaxationTimeInHours}:${relaxationTimeInRemainingMinutes}`;

  let relaxationTimeObj = parseTime(relaxationTime);

  let TimeWithRelaxation = new Date();
  TimeWithRelaxation.setHours(
    relaxationTimeObj.hours + parseInt(time.split(":")[0]),
    relaxationTimeObj.minutes + parseInt(time.split(":")[1]),
    0
  );

  let inTimeWithRelaxationFormatted = `${TimeWithRelaxation.getHours()}:${TimeWithRelaxation.getMinutes()}`;
  return inTimeWithRelaxationFormatted;
};

// Get Total Late Time
const getNightUserTotalLateTime = (
  inTime,
  outTime,
  userInTime,
  userOutTime,
  userRelaxationMinutes,
  userRules,
  selectedDevice
) => {
  let totalLateTimeValue = 0.0;

  let applyUserLateTimeRule = userRules?.lateTime?.isChecked;

  /**
   * Required
   * 1. Get inTime with relaxation
   * 2. Compare userInTime with inTimeWithRelaxation
   * 3. If userInTime is later than inTimeWithRelaxation then calculate late time
   */

  let inTimeWithRelaxationFormatted = getTimeWithRelaxation(
    userInTime,
    userRelaxationMinutes,
    applyUserLateTimeRule,
    selectedDevice?.relaxationMinutes
  );

  // // check if user is early then inTimeWithRelaxationFormatted
  let isEarly = isFirstInputEarlier(inTime, inTimeWithRelaxationFormatted);
  // If user is early then return 0.0

  let calculatedInTime = isEarly ? userInTime : inTime;

  let totalExpectedHours = getNightUserTotalHours(userInTime, userOutTime);
  let totalHoursWorked = getNightUserTotalHours(calculatedInTime, outTime);

  // get total late time only if totalHoursWorked is less than totalExpectedHours
  if (totalHoursWorked < totalExpectedHours) {
    let lateTime = totalExpectedHours - totalHoursWorked;

    return lateTime.toFixed(2);
  }

  return 0.0;
};

// Get Total overTime
const getNightUserTotalOvertime = (
  inTime,
  outTime,
  userInTime,
  userOutTime,
  overTimeAfterMinutes,
  userRules,
  selectedDevice
) => {
  let applyUserOverTimeRule = userRules?.overTime?.isChecked;

  const outTimeWithRelaxationFormatted = getTimeWithRelaxation(
    userOutTime,
    overTimeAfterMinutes,
    applyUserOverTimeRule,
    selectedDevice?.overtimeAfterMinutes
  );

  let totalOverTimeValue = 0.0;
  const totalHoursWorked = getNightUserTotalHours(inTime, outTime);

  // console.log("outTime", outTime);
  // console.log("outTimeWithRelaxationFormatted", outTimeWithRelaxationFormatted);
  // console.log("---------------------------");

  const totalExpectedHours = getNightUserTotalHours(userInTime, userOutTime);

  let totalExpectedHoursWithRelaxation = getNightUserTotalHours(
    userInTime,
    outTimeWithRelaxationFormatted
  );

  // If overtime rule is applied, Calculate overtime after expected hours + overtimeAfterMinutes
  // Calculate overtime only if totalHoursWorked is greater than totalExpectedHours
  if (totalHoursWorked > totalExpectedHoursWithRelaxation) {
    // Calculate overtime after expected hours + overtimeAfterMinutes
    const overtime = totalHoursWorked - totalExpectedHours;
    totalOverTimeValue = parseFloat(overtime.toFixed(2)) || 0.0;
  }

  return totalOverTimeValue || 0.0;
};

// Function to calculate total hours worked by a night user in Decimal format
const getNightUserTotalHours = (inTime, outTime) => {
  // inTime = "19:00", outTime = "01:00"
  /**
   * Calculate total hours by (total in-time hours + out-time hours)
   * Example:
   * In-Time: 19:00 to Midnight (24:00) = 5 hours
   * Out-Time: Midnight (00:00) to 01:00 = 1 hour
   * Total Hours = 5 + 1 = 6 hours
   */

  // Get in-time hours
  let { hours: inHours, minutes: inMinutes } = parseTime(inTime);
  inHours = 24 - inHours; // Convert to hours till midnight

  // Get out-time hours
  let { hours: outHours, minutes: outMinutes } = parseTime(outTime);

  let totalHours = inHours + outHours + outMinutes / 60 - inMinutes / 60;
  return totalHours.toFixed(2);
};

// Export all functions with named exports (ES Modules style)
export {
  getNightUserTotalHours,
  getNightUserTotalLateTime,
  getNightUserTotalOvertime,
};
